import { MapPin, Phone } from "lucide-react";
import {
  ADDRESS_1,
  ADDRESS_2,
  MAPS_EMBED,
  MAPS_URL,
  PHONE_DISPLAY,
  PHONE_TEL,
  btnLine,
  btnRust,
} from "./data";
import { Chapter, Rise } from "./motion";

const HOURS = [
  { days: "Monday — Friday", time: "10:00 AM — 6:00 PM" },
  { days: "Saturday", time: "10:00 AM — 5:00 PM" },
  { days: "Sunday", time: "Closed", closed: true },
];

const Contact = () => (
  <>
    <section
      id="contact"
      data-testid="contact-section"
      className="map-grid border-b-2 border-ink bg-paper"
    >
      <div className="mx-auto max-w-[1500px] px-4 py-20 sm:px-6 lg:py-28">
        <Rise>
          <Chapter num="07" label="Location & contact" />
        </Rise>
        <Rise delay={0.1}>
          <h2 className="mt-6 font-display text-[14vw] uppercase leading-[0.85] sm:text-8xl lg:text-[10rem]">
            Come <span className="text-stroke-ink">find</span> us.
          </h2>
        </Rise>

        <Rise delay={0.2}>
          <div className="relative mx-auto mt-14 max-w-2xl -rotate-1">
            <div className="tape -top-3 left-1/2 z-10 -translate-x-1/2 rotate-2" />
            <div
              data-testid="location-flyer"
              className="border-2 border-ink bg-cream p-8 shadow-hard sm:p-12"
            >
              <div className="border-2 border-dashed border-ink/40 p-6 sm:p-8">
                <p className="font-mono2 text-[11px] font-bold uppercase tracking-[0.35em] text-polered">
                  You are here → St Kilda
                </p>
                <h3 className="mt-4 font-display text-4xl uppercase sm:text-5xl">
                  Oliver&rsquo;s Barber Shop
                </h3>
                <address
                  data-testid="contact-address"
                  className="mt-4 text-xl font-semibold not-italic"
                >
                  {ADDRESS_1}
                </address>
                <p className="mt-1 font-mono2 text-xs uppercase tracking-[0.2em] text-ink/60">
                  {ADDRESS_2}
                </p>

                <a
                  href={PHONE_TEL}
                  data-testid="contact-phone-link"
                  className="mt-6 inline-flex items-center gap-3 font-display text-4xl text-polered transition-colors hover:text-ink sm:text-5xl"
                >
                  <Phone className="h-8 w-8" strokeWidth={2.5} />
                  {PHONE_DISPLAY}
                </a>

                <div className="mt-6 inline-block -rotate-2 border-2 border-ink bg-ink px-4 py-2">
                  <span className="font-hand text-xl text-paper">
                    Walk ins preferred
                  </span>
                </div>
                <p className="mt-3 text-sm font-medium text-ink/70">
                  Appointments can also be made by phone.
                </p>
                <p
                  data-testid="cash-note"
                  className="mt-4 inline-block rotate-1 border-2 border-dashed border-ink/50 bg-paper px-3 py-1.5 font-hand text-lg text-polered"
                >
                  Cash is king — cash preferred
                </p>

                <div className="mt-8 flex flex-col gap-4 sm:flex-row">
                  <a
                    href={MAPS_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="directions-button"
                    className={btnRust}
                  >
                    <MapPin size={20} /> Get directions
                  </a>
                  <a
                    href={PHONE_TEL}
                    data-testid="contact-call-button"
                    className={btnLine}
                  >
                    <Phone size={18} /> Call {PHONE_DISPLAY}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </Rise>

        <Rise delay={0.25}>
          <div
            data-testid="map-embed"
            className="relative mt-16 rotate-1 border-2 border-ink shadow-hard"
          >
            <div className="tape -top-3 left-12 z-10 -rotate-3" />
            <iframe
              title="Map to Oliver's Barber Shop — 2A/75 Fitzroy St, St Kilda (entrance via Jackson St)"
              src={MAPS_EMBED}
              data-testid="map-iframe"
              className="grit block h-[380px] w-full border-0 sm:h-[440px]"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
            <span className="absolute bottom-4 left-4 -rotate-2 border-2 border-ink bg-paper px-3 py-1 font-hand text-lg text-polered shadow-hard-sm">
              Entrance via Jackson St
            </span>
          </div>
        </Rise>
      </div>
    </section>

    <section
      id="hours"
      data-testid="hours-section"
      className="border-b-2 border-ink bg-cream"
    >
      <div className="mx-auto max-w-[1500px] px-4 py-20 sm:px-6 lg:py-28">
        <Rise>
          <Chapter num="08" label="Opening hours" />
        </Rise>
        <Rise delay={0.1}>
          <h2 className="mt-6 font-display text-[13vw] uppercase leading-[0.85] sm:text-8xl lg:text-[9rem]">
            When we&rsquo;re in
          </h2>
        </Rise>

        <div className="mt-12 max-w-3xl">
          {HOURS.map((h, i) => (
            <Rise key={h.days} delay={0.08 * i}>
              <div
                data-testid={`hours-row-${i}`}
                className="flex items-baseline border-b-2 border-ink/15 py-5"
              >
                <span className="font-display text-2xl uppercase sm:text-4xl">
                  {h.days}
                </span>
                <span className="mx-4 flex-1 border-b-2 border-dotted border-ink/40" />
                <span
                  className={`font-mono2 text-sm font-bold uppercase tracking-wider sm:text-lg ${
                    h.closed ? "text-polered" : ""
                  }`}
                >
                  {h.time}
                </span>
              </div>
            </Rise>
          ))}
        </div>

        <Rise delay={0.3}>
          <p className="mt-8 font-hand text-2xl text-polered">
            Walk ins preferred — just rock up.
          </p>
        </Rise>
      </div>
    </section>
  </>
);

export default Contact;
